import {
  OPERATOR_TYPES_1,
  OPERATOR_TYPES_3,
  OPERATOR_TYPES_4,
} from 'dashboard/routes/dashboard/settings/automation/operators';
import {
  CONDITION_ATTRIBUTE_GROUPS,
  SUGAR_TRIGGERS,
  SUGAR_TRIGGER_EVENT,
} from 'dashboard/routes/dashboard/settings/automation/constants';
import {
  DEFAULT_MESSAGE_CREATED_CONDITION,
  DEFAULT_CONVERSATION_OPENED_CONDITION,
  DEFAULT_OTHER_CONDITION,
  DEFAULT_ACTIONS,
  MESSAGE_CONDITION_VALUES,
  PRIORITY_CONDITION_VALUES,
  OPERATORS_WITHOUT_VALUE,
} from 'dashboard/constants/automation';
import { generateValues } from './filterQueryGenerator';
import actionQueryGenerator from './actionQueryGenerator';

// ---- Sugar triggers ----
//
// "Priority changed" and friends read plainly in the UI but are stored as the
// plain conversation_updated event plus a leading attribute_changed condition
// that asserts nothing about the new value. That value-less form is what makes
// the pattern recognisable: the condition editor always requires a value, so
// no hand-built condition can look like one.

const sugarTriggerFor = key =>
  SUGAR_TRIGGERS.find(trigger => trigger.key === key);

/**
 * Whether an event is one of the triggers that already asserts a change.
 * @param {string} eventName - The event chosen in the form.
 * @returns {boolean} True for a sugar trigger.
 */
export const isSugarTrigger = eventName => Boolean(sugarTriggerFor(eventName));

const isSugarCondition = condition =>
  condition?.filter_operator === 'attribute_changed' &&
  !condition.values?.to?.length &&
  SUGAR_TRIGGERS.some(trigger => trigger.attribute === condition.attribute_key);

/**
 * Turn a sugar trigger back into the event and condition actually stored.
 * @param {Object} automation - The automation as edited in the form.
 * @returns {Object} The automation with a real event name and conditions.
 */
export const expandSugarTrigger = automation => {
  const trigger = sugarTriggerFor(automation.event_name);
  if (!trigger) return automation;

  const marker = {
    attribute_key: trigger.attribute,
    filter_operator: 'attribute_changed',
    values: { from: [], to: [] },
    query_operator: automation.conditions.length ? 'and' : null,
    custom_attribute_type: '',
  };

  return {
    ...automation,
    event_name: SUGAR_TRIGGER_EVENT,
    conditions: [marker, ...automation.conditions],
  };
};

/**
 * Recognise a stored rule as a sugar trigger so editing shows what was picked.
 * @param {Object} automation - The automation as returned by the API.
 * @returns {Object} The automation with the sugar event and the marker removed.
 */
export const collapseSugarTrigger = automation => {
  if (automation.event_name !== SUGAR_TRIGGER_EVENT) return automation;

  const [first, ...rest] = automation.conditions ?? [];
  if (!isSugarCondition(first)) return automation;

  const trigger = SUGAR_TRIGGERS.find(
    item => item.attribute === first.attribute_key
  );
  return {
    ...automation,
    event_name: trigger.key,
    conditions: rest.map((condition, index) =>
      index === rest.length - 1
        ? { ...condition, query_operator: null }
        : condition
    ),
  };
};

/**
 * The event a sugar trigger stands for, used to look up available conditions.
 * @param {string} eventName - The event chosen in the form.
 * @returns {string} The event name the rule is actually stored under.
 */
export const storedEventName = eventName =>
  sugarTriggerFor(eventName) ? SUGAR_TRIGGER_EVENT : eventName;

export const getCustomAttributeInputType = key => {
  const customAttributeMap = {
    date: 'date',
    text: 'plain_text',
    list: 'search_select',
    checkbox: 'search_select',
  };

  return customAttributeMap[key] || 'plain_text';
};

export const isACustomAttribute = (customAttributes, key) => {
  return customAttributes.find(attr => {
    return attr.attribute_key === key;
  });
};

export const getCustomAttributeListDropdownValues = (
  customAttributes,
  type
) => {
  return customAttributes
    .find(attr => attr.attribute_key === type)
    .attribute_values.map(item => {
      return {
        id: item,
        name: item,
      };
    });
};

export const isCustomAttributeCheckbox = (customAttributes, key) => {
  return customAttributes.find(attr => {
    return (
      attr.attribute_key === key && attr.attribute_display_type === 'checkbox'
    );
  });
};

export const isCustomAttributeList = (customAttributes, type) => {
  return customAttributes.find(attr => {
    return (
      attr.attribute_key === type && attr.attribute_display_type === 'list'
    );
  });
};

export const getOperatorTypes = key => {
  const operatorMap = {
    list: OPERATOR_TYPES_1,
    text: OPERATOR_TYPES_3,
    number: OPERATOR_TYPES_1,
    link: OPERATOR_TYPES_1,
    date: OPERATOR_TYPES_4,
    checkbox: OPERATOR_TYPES_1,
  };

  return operatorMap[key] || OPERATOR_TYPES_1;
};

export const generateCustomAttributeTypes = (customAttributes, type) => {
  return customAttributes.map(attr => {
    return {
      key: attr.attribute_key,
      name: attr.attribute_display_name,
      inputType: getCustomAttributeInputType(attr.attribute_display_type),
      filterOperators: getOperatorTypes(attr.attribute_display_type),
      customAttributeType: type,
    };
  });
};

export const generateConditionOptions = (options, key = 'id') => {
  if (!options || !Array.isArray(options)) return [];
  return options.map(i => {
    return {
      id: i[key],
      name: i.title,
    };
  });
};

export const getActionOptions = ({
  agents,
  teams,
  labels,
  slaPolicies,
  type,
  addNoneToListFn,
}) => {
  const actionsMap = {
    assign_agent: addNoneToListFn ? addNoneToListFn(agents) : agents,
    assign_team: addNoneToListFn ? addNoneToListFn(teams) : teams,
    send_email_to_team: teams,
    add_label: generateConditionOptions(labels, 'title'),
    remove_label: generateConditionOptions(labels, 'title'),
    change_priority: PRIORITY_CONDITION_VALUES,
    add_sla: slaPolicies,
  };
  return actionsMap[type];
};

export const getConditionOptions = ({
  agents,
  booleanFilterOptions,
  campaigns,
  contacts,
  countries,
  customAttributes,
  inboxes,
  labels,
  languages,
  statusFilterOptions,
  teams,
  type,
}) => {
  if (isCustomAttributeCheckbox(customAttributes, type)) {
    return booleanFilterOptions;
  }

  if (isCustomAttributeList(customAttributes, type)) {
    return getCustomAttributeListDropdownValues(customAttributes, type);
  }

  const conditionFilterMaps = {
    status: statusFilterOptions,
    assignee_id: agents,
    contact: contacts,
    inbox_id: inboxes,
    team_id: teams,
    campaigns: generateConditionOptions(campaigns),
    browser_language: languages,
    conversation_language: languages,
    country_code: countries,
    message_type: MESSAGE_CONDITION_VALUES,
    priority: PRIORITY_CONDITION_VALUES,
    labels: generateConditionOptions(labels, 'title'),
  };

  return conditionFilterMaps[type];
};

export const getFileName = (action, files = []) => {
  const blobId = action.action_params[0];
  if (!blobId) return '';
  if (action.action_name === 'send_attachment') {
    const file = files.find(item => item.blob_id === blobId);
    if (file) return file.filename.toString();
  }
  return '';
};

export const getDefaultConditions = eventName => {
  if (eventName === 'message_created') {
    return DEFAULT_MESSAGE_CREATED_CONDITION;
  }
  if (eventName === 'conversation_opened') {
    return DEFAULT_CONVERSATION_OPENED_CONDITION;
  }
  return DEFAULT_OTHER_CONDITION;
};

export const getDefaultActions = () => {
  return DEFAULT_ACTIONS;
};

export const filterCustomAttributes = customAttributes => {
  return customAttributes.map(attr => {
    return {
      key: attr.attribute_key,
      name: attr.attribute_display_name,
      type: attr.attribute_display_type,
    };
  });
};

export const getStandardAttributeInputType = (automationTypes, event, key) => {
  if (event === 'team_assigned' && key === 'team_id') return 'multi_select';

  return automationTypes[storedEventName(event)].conditions.find(
    item => item.key === key
  ).inputType;
};

// Walks the conditions tree, turning each condition's UI value into the shape
// the API stores. The last entry of every list drops its query operator, since
// there is nothing after it to join to.
const generateConditionsPayload = nodes =>
  nodes.map((node, index) => {
    const queryOperator =
      index === nodes.length - 1 ? undefined : (node.query_operator ?? 'and');

    if (Array.isArray(node.conditions)) {
      return {
        query_operator: queryOperator,
        conditions: generateConditionsPayload(node.conditions),
      };
    }

    return {
      ...node,
      values: generateValues(node),
      query_operator: queryOperator,
    };
  });

export const generateAutomationPayload = payload => {
  const automation = expandSugarTrigger(JSON.parse(JSON.stringify(payload)));
  automation.conditions = generateConditionsPayload(automation.conditions);
  automation.actions = actionQueryGenerator(automation.actions);
  return automation;
};

export const isCustomAttribute = (attrs, key) => {
  return attrs.find(attr => attr.key === key);
};

export const generateCustomAttributes = (
  conversationAttributes = [],
  contactAttributes = []
) => {
  return [...conversationAttributes, ...contactAttributes];
};

/**
 * Split a flat list of condition attributes into the groups rendered as
 * `<optgroup>`s in the dropdown. Groups keep the order declared in
 * CONDITION_ATTRIBUTE_GROUPS; empty ones are dropped.
 * @param {Array} attributes - Flat list of condition attributes.
 * @returns {Array} Groups of the shape { name, attributes }.
 */
export const groupConditionAttributes = attributes => {
  return CONDITION_ATTRIBUTE_GROUPS.map(group => ({
    name: group.name,
    attributes: attributes.filter(attribute =>
      group.customAttributeType
        ? attribute.customAttributeType === group.customAttributeType
        : group.attributes.includes(attribute.key)
    ),
  })).filter(group => group.attributes.length);
};

/**
 * Get attributes for a given key from automation types.
 * @param {Object} automationTypes - Object containing automation types.
 * @param {string} key - The key to get attributes for.
 * @returns {Array} Array of condition objects for the given key.
 */
export const getAttributes = (automationTypes, key) => {
  return automationTypes[storedEventName(key)].conditions;
};

/**
 * Get the automation type for a given key.
 * @param {Object} automationTypes - Object containing automation types.
 * @param {Object} automation - The automation object.
 * @param {string} key - The key to get the automation type for.
 * @returns {Object} The automation type object.
 */
export const getAutomationType = (automationTypes, automation, key) => {
  return automationTypes[
    storedEventName(automation.event_name)
  ].conditions.find(condition => condition.key === key);
};

/**
 * Get the input type for a given key.
 * @param {Array} allCustomAttributes - Array of all custom attributes.
 * @param {Object} automationTypes - Object containing automation types.
 * @param {Object} automation - The automation object.
 * @param {string} key - The key to get the input type for.
 * @returns {string} The input type.
 */
export const getInputType = (
  allCustomAttributes,
  automationTypes,
  automation,
  key
) => {
  const customAttribute = isACustomAttribute(allCustomAttributes, key);
  if (customAttribute) {
    return getCustomAttributeInputType(customAttribute.attribute_display_type);
  }
  return getStandardAttributeInputType(
    automationTypes,
    automation.event_name,
    key
  );
};

/**
 * Get operators for a given key.
 * @param {Array} allCustomAttributes - Array of all custom attributes.
 * @param {Object} automationTypes - Object containing automation types.
 * @param {Object} automation - The automation object.
 * @param {string} mode - The mode ('edit' or other).
 * @param {string} key - The key to get operators for.
 * @returns {Array} Array of operators.
 */
export const getOperators = (
  allCustomAttributes,
  automationTypes,
  automation,
  mode,
  key
) => {
  if (mode === 'edit') {
    const customAttribute = isACustomAttribute(allCustomAttributes, key);
    if (customAttribute) {
      return getOperatorTypes(customAttribute.attribute_display_type);
    }
  }
  const type = getAutomationType(automationTypes, automation, key);
  // "Changed to" only makes sense on the trigger that fires for any change; a
  // sugar trigger has already pinned down which attribute changed.
  if (automation.event_name === SUGAR_TRIGGER_EVENT)
    return type.filterOperators;

  return type.filterOperators.filter(
    operator => operator.value !== 'attribute_changed'
  );
};

/**
 * Get the custom attribute type for a given key.
 * @param {Object} automationTypes - Object containing automation types.
 * @param {Object} automation - The automation object.
 * @param {string} key - The key to get the custom attribute type for.
 * @returns {string} The custom attribute type.
 */
export const getCustomAttributeType = (automationTypes, automation, key) => {
  return automationTypes[
    storedEventName(automation.event_name)
  ].conditions.find(i => i.key === key).customAttributeType;
};

/**
 * Determine if an action input should be shown.
 * @param {Array} automationActionTypes - Array of automation action type objects.
 * @param {string} action - The action to check.
 * @returns {boolean} True if the action input should be shown, false otherwise.
 */
export const showActionInput = (automationActionTypes, action) => {
  if (action === 'send_email_to_team' || action === 'send_message')
    return false;
  const type = automationActionTypes.find(i => i.key === action).inputType;
  return !!type;
};

/**
 * Determine if a condition should show an input for its value.
 * @param {string} filterOperator - The filter operator of the condition.
 * @returns {boolean} False for operators that only assert presence.
 */
export const showConditionValueInput = filterOperator => {
  return !OPERATORS_WITHOUT_VALUE.includes(filterOperator);
};

/**
 * Whether a node in the conditions tree is a group rather than a condition.
 * @param {Object} node - A conditions tree node.
 * @returns {boolean} True when the node holds nested conditions.
 */
export const isConditionGroup = node => Array.isArray(node?.conditions);

/**
 * Number every condition in the tree, depth first, so rows and the formula
 * shown under them refer to the same numbers.
 * @param {Array} nodes - The conditions tree.
 * @returns {Array} Flat list of conditions in display order.
 */
export const flattenConditions = (nodes = []) =>
  nodes.flatMap(node => (isConditionGroup(node) ? node.conditions : [node]));

const AND = 'and';

/**
 * Join labels the way Postgres reads them: AND binds tighter than OR, so a
 * run of ANDs surrounded by ORs gets its own parentheses.
 * @param {Array} items - Entries of { label, queryOperator }.
 * @param {Object} words - Localised { and, or } words.
 * @returns {string} The joined expression.
 */
const joinByPrecedence = (items, words) => {
  if (!items.length) return '';

  // Split into runs of AND, which OR separates.
  const runs = [[items[0]]];
  items.slice(0, -1).forEach((item, index) => {
    const isAnd = String(item.queryOperator ?? AND).toLowerCase() !== 'or';
    if (isAnd) runs[runs.length - 1].push(items[index + 1]);
    else runs.push([items[index + 1]]);
  });

  const joined = runs.map(run =>
    run.map(item => item.label).join(` ${words.and} `)
  );

  // A single run needs no parentheses — there is no OR to bind against.
  if (joined.length === 1) return joined[0];

  return joined
    .map((run, index) => (runs[index].length > 1 ? `(${run})` : run))
    .join(` ${words.or} `);
};

/**
 * Render the conditions tree as the boolean expression Postgres will evaluate,
 * with each condition shown as its row number.
 * @param {Array} nodes - The conditions tree.
 * @param {Object} words - Localised { and, or } words.
 * @returns {string} e.g. "1 AND (2 OR 3)".
 */
export const buildConditionFormula = (nodes, words) => {
  let number = 0;

  const items = nodes.map(node => {
    if (!isConditionGroup(node)) {
      number += 1;
      return { label: String(number), queryOperator: node.query_operator };
    }

    const inner = node.conditions.map(condition => {
      number += 1;
      return { label: String(number), queryOperator: condition.query_operator };
    });

    return {
      label: inner.length ? `(${joinByPrecedence(inner, words)})` : '',
      queryOperator: node.query_operator,
    };
  });

  return joinByPrecedence(
    items.filter(item => item.label),
    words
  );
};
